import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { fetchWorkAreas, type WorkAreaOption } from 'modules/admin/api/plotPlanService'
import { PERMIT_STATUS_CODES } from 'modules/permit/constants/permitStatus'
import {
  closePermit,
  closePermitsBulk,
  getClosePermits,
  type ClosePermitBulkRequestPayload,
  type ClosePermitRequestPayload,
  type OpenPermitListItem,
} from 'modules/permit/api/permitService'
import { createDefaultFilters, DEFAULT_TENANT_ID, PAGE_SIZE } from './constants'
import type { ClosePermitStats, FilterState, LayoutState } from './types'
import { getErrorMessage, isOpenedPermitStatus, normalizeGuid, resolveAccessibleLocationIds, resolveMenuRoleId } from './utils'
import { requestNavCountsRefresh } from 'modules/permit/utils/navCountRefresh'
import { getPersistedSelectedSiteId, resolveTenantCandidate, resolveTenantId } from 'shared/utils/tenantContext'

const ALL_CLOSE_PAGE_STATUSES = [PERMIT_STATUS_CODES.OPEN, PERMIT_STATUS_CODES.CLOSED] as const
const COMBINED_STATUS_FETCH_LIMIT = 500
const ALLOWED_CLOSE_PERMIT_TYPES = ['HOT_WORK', 'COLD_WORK', 'CONFINE_WORK'] as const

const getPermitSortTime = (permit: OpenPermitListItem) => {
  const timestamp = Date.parse(
    permit.openedAt ??
    permit.plannedStart ??
    permit.plannedEnd ??
    '',
  )
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export function useClosePermitPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = useMemo(() => ((location.state as LayoutState | undefined) ?? {}), [location.state])
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])
  const tenantId = resolveTenantId(state.site?.id, persistedSiteId, DEFAULT_TENANT_ID)
  const siteId = resolveTenantCandidate(state.site?.id, persistedSiteId)

  const [filters, setFilters] = useState<FilterState>(createDefaultFilters)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(createDefaultFilters)
  const [permits, setPermits] = useState<OpenPermitListItem[]>([])
  const [workAreaOptions, setWorkAreaOptions] = useState<WorkAreaOption[]>([])
  const [selectedPermitIds, setSelectedPermitIds] = useState<string[]>([])
  const [closeDialogPermitIds, setCloseDialogPermitIds] = useState<string[]>([])
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false)
  const [closeSummary, setCloseSummary] = useState('')
  const [isSubmittingClose, setIsSubmittingClose] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [statistics, setStatistics] = useState<ClosePermitStats>({
    opened: 0,
    closed: 0,
    coldWork: 0,
    hotWork: 0,
    confineWork: 0,
  })

  const currentUserId = useMemo(() => {
    const candidates = [
      state.user?.id,
      state.user?.user_id,
      state.user?.userId,
      state.user?.employee_id,
      state.user?.employeeId,
      state.user?.emp_id,
      state.user?.empId,
    ]
    for (const candidate of candidates) {
      const normalized = normalizeGuid(candidate)
      if (normalized) return normalized
    }
    return null
  }, [state.user])

  const activeRoleId = useMemo(
    () => resolveMenuRoleId(state.menus, '/permit-close'),
    [state.menus],
  )

  const accessibleLocationIds = useMemo(
    () => resolveAccessibleLocationIds(state.user, state.roles, activeRoleId),
    [activeRoleId, state.roles, state.user],
  )
  const selectedLocationIds = useMemo(() => {
    if (appliedFilters.workAreaId && appliedFilters.workAreaId !== 'all') return [appliedFilters.workAreaId]
    return undefined
  }, [appliedFilters.workAreaId])

  useEffect(() => {
    if (!state.user) navigate('/modules', { replace: true })
  }, [navigate, state.user])

  useEffect(() => {
    let cancelled = false
    fetchWorkAreas(tenantId)
      .then((items) => {
        if (cancelled) return
        const sourceItems = items ?? []
        const nextItems = accessibleLocationIds.length > 0
          ? sourceItems.filter((item) => accessibleLocationIds.includes(item.locationId.toLowerCase()))
          : []
        setWorkAreaOptions(nextItems)
      })
      .catch((error) => {
        console.error('PermitClosePage: failed to load work areas', error)
        if (!cancelled) setWorkAreaOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [accessibleLocationIds, tenantId])

  const loadPermits = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const baseQuery = {
        tenantId,
        activeRoleId: activeRoleId ?? undefined,
        siteId,
        locationIds: selectedLocationIds,
        permitType: appliedFilters.permitType,
        searchTerm: appliedFilters.search.trim() || undefined,
        plannedStartFrom: appliedFilters.workDate || undefined,
        plannedStartTo: appliedFilters.workDate || undefined,
      }

      const statusesToFetch =
        appliedFilters.status === 'all' ? [...ALL_CLOSE_PAGE_STATUSES] : [appliedFilters.status]
      const permitTypesToFetch =
        appliedFilters.permitType === 'all' ? [...ALLOWED_CLOSE_PERMIT_TYPES] : [appliedFilters.permitType]

      if (statusesToFetch.length > 1 || permitTypesToFetch.length > 1) {
        const requestCombos = statusesToFetch.flatMap((status) =>
          permitTypesToFetch.map((permitType) => ({ status, permitType })),
        )
        const responses = await Promise.all(
          requestCombos.map(({ status, permitType }) =>
            getClosePermits({
              ...baseQuery,
              status,
              permitType,
              page: 1,
              pageSize: COMBINED_STATUS_FETCH_LIMIT,
            }),
          ),
        )

        const mergedItems = responses
          .flatMap((response) => response.items ?? [])
          .filter((permit, index, items) => items.findIndex((item) => item.permitId === permit.permitId) === index)
          .sort((a, b) => getPermitSortTime(b) - getPermitSortTime(a))

        const nextStatistics = requestCombos.reduce<ClosePermitStats>(
          (accumulator, combo, index) => {
            const response = responses[index]
            const count = response?.totalItems ?? response?.items?.length ?? 0

            if (combo.status === PERMIT_STATUS_CODES.OPEN) {
              accumulator.opened += count
            } else if (combo.status === PERMIT_STATUS_CODES.CLOSED) {
              accumulator.closed += count
            }

            if (combo.permitType === 'COLD_WORK') {
              accumulator.coldWork += count
            } else if (combo.permitType === 'HOT_WORK') {
              accumulator.hotWork += count
            } else if (combo.permitType === 'CONFINE_WORK') {
              accumulator.confineWork += count
            }

            return accumulator
          },
          {
            opened: 0,
            closed: 0,
            coldWork: 0,
            hotWork: 0,
            confineWork: 0,
          },
        )

        const startIndex = (pageNumber - 1) * PAGE_SIZE
        const endIndex = startIndex + PAGE_SIZE
        setPermits(mergedItems.slice(startIndex, endIndex))
        setTotalItems(nextStatistics.opened + nextStatistics.closed)
        setStatistics(nextStatistics)
      } else {
        const response = await getClosePermits({
          ...baseQuery,
          status: statusesToFetch[0],
          permitType: permitTypesToFetch[0],
          page: pageNumber,
          pageSize: PAGE_SIZE,
        })
        const responseTotal = response.totalItems ?? response.items?.length ?? 0
        setPermits(response.items ?? [])
        setTotalItems(responseTotal)
        setStatistics({
          opened: statusesToFetch[0] === PERMIT_STATUS_CODES.OPEN ? responseTotal : 0,
          closed: statusesToFetch[0] === PERMIT_STATUS_CODES.CLOSED ? responseTotal : 0,
          coldWork: permitTypesToFetch[0] === 'COLD_WORK' ? responseTotal : 0,
          hotWork: permitTypesToFetch[0] === 'HOT_WORK' ? responseTotal : 0,
          confineWork: permitTypesToFetch[0] === 'CONFINE_WORK' ? responseTotal : 0,
        })
      }
    } catch (error) {
      console.error('PermitClosePage: failed to load permits', error)
      setPermits([])
      setTotalItems(0)
      setStatistics({
        opened: 0,
        closed: 0,
        coldWork: 0,
        hotWork: 0,
        confineWork: 0,
      })
      setLoadError(getErrorMessage(error, 'ไม่สามารถโหลดรายการ Permit ได้'))
    } finally {
      setIsLoading(false)
    }
  }, [activeRoleId, appliedFilters.permitType, appliedFilters.search, appliedFilters.status, appliedFilters.workDate, pageNumber, selectedLocationIds, siteId, tenantId])

  useEffect(() => {
    loadPermits()
  }, [loadPermits])

  const selectablePermits = useMemo(() => permits.filter((permit) => isOpenedPermitStatus(permit.status)), [permits])

  useEffect(() => {
    setSelectedPermitIds((prev) => prev.filter((id) => selectablePermits.some((permit) => permit.permitId === id)))
  }, [selectablePermits])

  const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1)
  const isAllSelected = selectablePermits.length > 0 && selectedPermitIds.length === selectablePermits.length
  const closeDialogPermits = useMemo(() => permits.filter((permit) => closeDialogPermitIds.includes(permit.permitId)), [closeDialogPermitIds, permits])

  const applyFilters = useCallback((nextFilters: FilterState) => {
    if (pageNumber !== 1) setPageNumber(1)
    setAppliedFilters({ ...nextFilters })
  }, [pageNumber])

  const handleSearch = useCallback(() => {
    applyFilters(filters)
  }, [applyFilters, filters])

  const resetFilters = useCallback(() => {
    const nextFilters = { ...createDefaultFilters(), workDate: '' }
    setFilters(nextFilters)
    applyFilters(nextFilters)
  }, [applyFilters])

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    setSelectedPermitIds(checked ? selectablePermits.map((permit) => permit.permitId) : [])
  }, [selectablePermits])

  const togglePermitSelection = useCallback((permitId: string, checked: boolean) => {
    const permit = selectablePermits.find((item) => item.permitId === permitId)
    if (!permit) return
    setSelectedPermitIds((prev) => checked ? (prev.includes(permitId) ? prev : [...prev, permitId]) : prev.filter((id) => id !== permitId))
  }, [selectablePermits])

  const openCloseDialog = useCallback((permitIds: string[]) => {
    const validPermitIds = permitIds.filter((permitId) => selectablePermits.some((permit) => permit.permitId === permitId))
    if (validPermitIds.length === 0) {
      toast.error('เลือกรายการ Permit ที่อยู่สถานะ Opened ก่อน')
      return
    }

    if (!currentUserId) {
      toast.error('ไม่พบรหัสผู้ใช้งานสำหรับบันทึกการปิด Permit')
      return
    }

    setCloseDialogPermitIds(validPermitIds)
    setCloseSummary('')
    setIsCloseDialogOpen(true)
  }, [currentUserId, selectablePermits])

  const handleViewPermit = useCallback((permit: OpenPermitListItem) => {
    navigate('/permit-request', {
      state: {
        ...state,
        mode: 'edit',
        permitId: permit.permitId,
        entryMode: 'close',
        hideActionBar: false,
        forceReadOnly: false,
      },
    })
  }, [navigate, state])

  const handleSubmitClose = useCallback(async () => {
    if (closeDialogPermitIds.length === 0) return
    if (!currentUserId) {
      toast.error('ไม่พบรหัสผู้ใช้งานสำหรับบันทึกการปิด Permit')
      return
    }

    setIsSubmittingClose(true)
    try {
      if (closeDialogPermitIds.length === 1) {
        const payload: ClosePermitRequestPayload = {
          tenantId,
          permitId: closeDialogPermitIds[0],
          activeRoleId: activeRoleId ?? undefined,
          closedByUserId: currentUserId,
          summary: closeSummary || null,
          templateId: null,
          templateName: null,
          safetyObservation: null,
          actualStart: null,
          actualEnd: null,
          checklist: [],
        }
        await closePermit(closeDialogPermitIds[0], payload)
      } else {
        const payload: ClosePermitBulkRequestPayload = {
          tenantId,
          permitIds: closeDialogPermitIds,
          activeRoleId: activeRoleId ?? undefined,
          closedByUserId: currentUserId,
          summary: closeSummary || null,
          templateId: null,
          templateName: null,
          safetyObservation: null,
          actualStart: null,
          actualEnd: null,
          checklist: [],
        }
        await closePermitsBulk(payload)
      }

      toast.success(closeDialogPermitIds.length > 1 ? 'ปิด Permit หลายรายการเรียบร้อยแล้ว' : 'ปิด Permit เรียบร้อยแล้ว')
      setIsCloseDialogOpen(false)
      setCloseDialogPermitIds([])
      setSelectedPermitIds([])
      requestNavCountsRefresh()
      await loadPermits()
    } catch (error) {
      console.error('PermitClosePage: close permit failed', error)
      toast.error(getErrorMessage(error, 'ไม่สามารถปิด Permit ได้'))
    } finally {
      setIsSubmittingClose(false)
    }
  }, [activeRoleId, closeDialogPermitIds, closeSummary, currentUserId, loadPermits, tenantId])

  return {
    state,
    navigate,
    filters,
    setFilters,
    permits,
    workAreaOptions,
    selectedPermitIds,
    isCloseDialogOpen,
    setIsCloseDialogOpen,
    isSubmittingClose,
    isLoading,
    loadError,
    pageNumber,
    setPageNumber,
    totalItems,
    closeSummary,
    setCloseSummary,
    statistics,
    totalPages,
    isAllSelected,
    selectableCount: selectablePermits.length,
    closeDialogPermits,
    handleToggleSelectAll,
    togglePermitSelection,
    openCloseDialog,
    handleViewPermit,
    handleSubmitClose,
    handleSearch,
    resetFilters,
  }
}
